'use client';

// Настройки → одна строка «Подписка» открывает ЕДИНУЮ модалку биллинга
// (SCHEMA-EDITOR этап 9): статус подписки · оплата по матрице состояний · карта ·
// баланс AI · история. Матрица — чистый модуль billing-state.ts; оплата во ВСЕХ
// состояниях идёт на существующую хостед-страницу Robokassa (решение A1). Формы
// пополнения/перевода — billing-balance.tsx, общее — billing-shared.tsx.
import { webOnly, readPhoneLook } from './layout-mode';
import { useCallback, useEffect, useRef, useState, type ReactNode } from 'react';
import { useLocale, useTranslations } from 'next-intl';
import { isNativeApp, useNativeApp } from '@/lib/native-app';
import { useSlidePanel } from './native-layer-host';
import { TermsLayerAt } from './help-layer';
import { CheckSheet, type Check, type CheckGo } from './billing-check';
import { usePersistentPeek } from '@/lib/use-persistent-data';
import { okAccept, warmTarget } from '@/lib/warm';
import { PopSelect } from '@scriptera/ui';
import {
  KBareButton,
  KButton,
  KDisclosure,
  KListBlock,
  KListGroup,
  KListNote,
  KListRow,
  KListSection,
  KSwitch,
} from '@scriptera/ui/kit';
import { DError, DGroup } from './drawer-kit';
import { Sk } from './skeleton';
import { SettingsHero } from './settings-hero';
import { FHint, FPane } from './form-kit';
import { EDITOR_SHEET } from '@/lib/sheet-detent';
import { billingState } from '@/lib/billing-state';
import { billingViewOf } from '@/lib/billing-view';
import { OverdueSections } from './billing-views';
import { ProPerks, ProShowcase, accountOptionsFrom } from './billing-showcase';
// Лимит бесплатных аккаунтов — из общего модуля БЕЗ Prisma-клиента (страница клиентская).
import { FREE_ACCOUNT_LIMIT } from '@scriptera/db/paid-accounts';
import { paymentMoney } from '@scriptera/db/money';
import type { Currency } from '@scriptera/db/currency';
import { discountActive } from '@scriptera/db/terms';
import {
  addQuoteKey,
  addQuoteUrl,
  autorenewRowShown,
  autorenewStart,
  cardDisplay,
  cardSetupPath,
  subscriptionCurrency,
  nextCharge,
  payCalc,
  summaryNextCharge,
  PAYER_KEY,
  PAYER_URL,
  parseAddQuote,
  parsePayerResponse,
  parseWallets,
  payErrorKey,
  payMethodWordKey,
  payScreenReady,
  payStep,
  payTerms,
  periodPrice,
  savedCardUsable,
  stripeKeyOf,
  stripeReturnUrl,
  tokensPayAllowed,
  type PayAction,
  type PayPeriod,
  type PayStep,
  type PayTerms,
} from '@/lib/pay-flow';
import {
  AddCardSheet,
  cardSheetLock,
  confirmSavedStripeCard,
  PayLockNote,
  payErrorBuzz,
  StripeCardSheet,
  stripeErrorForClient,
  stripeFor,
  type CheckPayBody,
  type LayerPayBody,
  type PayNext,
} from './stripe-card-sheet';
import {
  awaitBilling,
  CardSetupWait,
  payViaRedirect,
  refreshBilling,
  PERIODS,
  postJson,
  useFmtDate,
  useFmtDateShort,
  useFmtDateTime,
  useFmtMoney,
  Tok,
  useSummary,
  type Period,
} from './billing-shared';
import { useTopupSheet } from './billing-balance';
import {
  catchPayReturn,
  consumePayReturn,
  setPayResultHistory,
  setPayResultRetry,
  usePayFrameSheet,
  usePayResultSheet,
} from './pay-result';
import type { PayResultData } from '@/lib/pay-result';

/** Сколько аккаунтов даёт бесплатный тариф (тот же лимит, что и на подключении). */
const FREE_ACCOUNTS = FREE_ACCOUNT_LIMIT;


/** Сколько аккаунтов можно выбрать в форме: обычный список, без счётчиков «−/+». */
const ACCOUNT_OPTIONS = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 15, 20, 30, 50];

/** Имя нижнего уровня пути оплаты, который снимают оплата, «×» кассы и ошибки (`closeFrom`): экран расчёта, докупка
 *  или — с витрины, где расчёта нет (TARIFF-PRO-2-TZ §3.4), — само подтверждение. */
type PayCloseKey = 'pay-screen' | 'pay-add' | 'pay-check';

const acctIcon = {
  fill: 'none' as const,
  stroke: 'currentColor',
  strokeWidth: 1.9,
  strokeLinecap: 'round' as const,
  strokeLinejoin: 'round' as const,
};

// ─────────────────────────── строка на «Настройках» ───────────────────────────

// ─────────── ССЫЛКА С ОТКРЫТИЕМ ОПЛАТЫ ?pay=1 (FUNNEL-TZ §3, П4) ───────────
// Просьба «сразу экран оплаты» лежит модулем: её ставит верх кабинета (BillingTopChrome), а
// исполняет ПЕРВАЯ «Подписка», у которой пришла сводка, — страница раздела (поле на десктопе,
// лист на телефоне) или окно подписки, открытое верхом на других страницах. Один раз на загрузку.
let autoPayWanted = false;
const autoPaySubs = new Set<() => void>();
function requestAutoPay(): void {
  autoPayWanted = true;
  for (const fn of autoPaySubs) fn();
}
/** Забрать просьбу (true — она была и теперь снята). */
function takeAutoPay(): boolean {
  if (!autoPayWanted) return false;
  autoPayWanted = false;
  return true;
}
// ─────────── ДОКУПКА ПРИ ПОДКЛЮЧЕНИИ СВЕРХ ОПЛАЧЕННЫХ (ТЗ ACCOUNTS-TOGGLE §3.3) ───────────
// Подключение на Pro упёрлось в `need_pay` → открывается ТОТ ЖЕ слой «Добавить аккаунты», что на
// «Тарифе» (компонент AddAccounts, не копия), с предзаданным числом и строкой «Оплачено {paid},
// нужно {need}». Просьбу исполняет первая «Подписка», у которой пришла сводка (как ?pay=1 выше).
/** Просьба открыть докупку: сколько добавить, сколько оплачено и нужно, и кому сказать, что слой
 *  закрылся (`bought` — нажимали ли оплату). */
export interface AddAccountsRequest {
  want: number;
  paid: number;
  need: number;
  onClosed: (bought: boolean) => void;
}
let addWanted: AddAccountsRequest | null = null;
const addSubs = new Set<() => void>();
function requestAddAccounts(r: AddAccountsRequest): void {
  addWanted = r;
  for (const fn of addSubs) fn();
}
function takeAddAccounts(): AddAccountsRequest | null {
  const r = addWanted;
  addWanted = null;
  return r;
}

/** Сколько ждать оплату после закрытия слоя докупки с нажатой оплатой (касса, 3-D Secure). */
const NEED_PAY_WAIT_MS = 3 * 60_000;
const NEED_PAY_POLL_MS = 3000;

/**
 * ПОДКЛЮЧЕНИЕ СВЕРХ ОПЛАЧЕННЫХ (ТЗ ACCOUNTS-TOGGLE §3.3, владелец 13.10.2026): одна рука на все
 * формы подключения. В браузере — докупка «Добавить аккаунты» с `want = need − paid`; оплата
 * прошла (сводка показала `paid ≥ need`) → `retry` повторяет подключение с того же места;
 * закрыли без оплаты или оплата не пришла → `onCancel` (форма пишет «Аккаунт не подключён:
 * нужна оплата»). В приложении оплаты нет (запрет №7) — сюда не зовут: формы показывают свою
 * плашку «на сайте».
 */
export function useNeedPayConnect(): (np: { paid: number; need: number }, retry: () => void, onCancel: () => void) => void {
  const openBilling = useBillingDrawer();
  return useCallback(
    (np, retry, onCancel) => {
      let done = false;
      const finish = (paid: boolean) => {
        if (done) return;
        done = true;
        if (paid) retry();
        else onCancel();
      };
      const watch = (deadline: number) => {
        const tick = async () => {
          if (done) return;
          const j = (await fetch('/api/billing/summary', { cache: 'no-store' })
            .then((r) => r.json())
            .catch(() => null)) as { accounts?: { paid?: number } } | null;
          if ((j?.accounts?.paid ?? 0) >= np.need) return finish(true);
          if (Date.now() > deadline) return finish(false);
          window.setTimeout(() => void tick(), NEED_PAY_POLL_MS);
        };
        void tick();
      };
      openBilling({
        add: {
          want: Math.max(1, np.need - np.paid),
          paid: np.paid,
          need: np.need,
          onClosed: (bought) => (bought ? watch(Date.now() + NEED_PAY_WAIT_MS) : finish(false)),
        },
      });
    },
    [openBilling],
  );
}

/** Хук: открыть единую модалку биллинга (используется и строкой, и «назад» из под-форм).
 *  `pay` — сразу открыть поверх неё экран оплаты (ссылка ?pay=1, FUNNEL-TZ §3). */
export function useBillingDrawer(): (o?: { pay?: boolean; add?: AddAccountsRequest }) => void {
  const drawer = useSlidePanel();
  const t = useTranslations('cabinet.billing');
  return useCallback(
    (o?: { pay?: boolean; add?: AddAccountsRequest }) => {
      if (o?.pay === true) requestAutoPay();
      if (o?.add) requestAddAccounts(o.add);
      drawer.open({
        title: t('billingTitle'),
        ...webOnly({ sheet: true }),
        lead: 'close', // вертикальный лист пути оплаты — «×» (PAY-FLOW-TZ §14.6, канон М83)
        body: <BillingPanel />,
      });
    },
    [drawer, t],
  );
}

/**
 * ПЛАШКА «ЛИМИТ БЕСПЛАТНОГО» — ОДНА НА КАБИНЕТ (владелец 06.09.2026, К17.4 п.1; с 18.09.2026
 * единственный повод — бот на аккаунте сверх бесплатных FREE_ACCOUNT_LIMIT: «подтверждения
 * подписки» и «всё выключено» больше нет, боты работают всегда).
 * Её показывают тумблер бота, тумблер цепочки в списке, страница цепочки и родное окно
 * приложения — поэтому она живёт здесь, рядом со штатным окном подписки.
 *
 * В БРАУЗЕРЕ — слова и кнопка, открывающая штатное окно подписки (второго платёжного
 * окна в кабинете нет). В ПРИЛОЖЕНИИ — ТОЛЬКО СЛОВА: ни цен, ни сумм, ни ссылок на оплату
 * (CLAUDE.md позиционирование п.7) — Apple снимает приложения за платёж мимо своего;
 * слова показывает хозяин (`note`) своим окном.
 */
export function useBillingPlate(note: (title: string, text: string) => void): (gate?: { title: string; text: string }) => void {
  const t = useTranslations('cabinet.automations');
  const tb = useTranslations('cabinet.billing');
  const panel = useSlidePanel();
  const native = useNativeApp();
  const openBilling = useBillingDrawer();
  // ЕДИНЫЙ ЗАМОК Pro (QUICK-START §11.31): отказ 402 `billing` дверей отправки несёт `gate` — заголовок «Доступно на
  // тарифе Pro» и текст про Pro (а не «боты на двух аккаунтах» — это другой замок); кнопка — «Подключить Pro».
  return (gate) => {
    const title = gate?.title ?? t('billOffTitle');
    if (native) {
      note(title, gate?.text ?? t('billOffApp'));
      return;
    }
    panel.open({
      title,
      ...EDITOR_SHEET,
      body: (
        <FPane>
          <FHint>{gate?.text ?? t('billOffText')}</FHint>
          <KButton
            tone="primary"
            onClick={() => {
              panel.close();
              openBilling();
            }}
          >
            {gate ? tb('proGate.connect') : t('billOffPay')}
          </KButton>
        </FPane>
      ),
    });
  };
}

// ─────────────── сквозная плашка скидки + ссылка с открытием оплаты ───────────────

/**
 * Верх кабинета на всех страницах: ссылка вида /app?pay=1 открывает модалку оплаты
 * сразу после загрузки (параметр из адреса стирается, чтобы не переоткрываться по
 * «назад»/обновлению).
 * Плашки-баннера акции здесь БОЛЬШЕ НЕТ (владелец 05.08: «не хочу ни при каких
 * обстоятельствах никаких баннеров видеть на сайте») — не возвращать ни под каким
 * условием без его прямого «да»; сама скидка жива и видна в модалке оплаты.
 */
// Лист «Результат оплаты» и лист «Окно кассы» (один экран на все пути оплаты, владелец 09.10 и
// 10.10.2026) живут в pay-result.tsx: usePayResultSheet / usePayFrameSheet; там же после успешной
// оплаты стирается `?pay=1` (dropPayParam, FUNNEL-TZ §3).

export function BillingTopChrome() {
  const native = useNativeApp();
  const openBilling = useBillingDrawer();
  const openHistory = usePaymentsDrawer();

  // «История платежей» на экране результата — его открывает верх кабинета (есть на всех страницах).
  useEffect(() => {
    if (native) return;
    return setPayResultHistory(openHistory);
  }, [native, openHistory]);

  // Диплинк ?pay=1 — один раз на загрузку страницы (StrictMode дважды зовёт эффект).
  const deepFired = useRef(false);
  useEffect(() => {
    if (native || deepFired.current) return;
    // Возврат с кассы (Робокасса `pay=result`, Stripe после 3-D Secure `pay=stripe`) ведёт на «Тариф»:
    // поймать номер платежа и вычистить адрес (наши метки и то, что дописал Stripe) до того, как
    // раздел «Аккаунт» перепишет адрес (Р5). Лист результата откроет сам «Тариф» (consumePayReturn).
    catchPayReturn();
    const url = new URL(window.location.href);
    if (url.searchParams.get('pay') !== '1') return;
    deepFired.current = true;
    // FUNNEL-TZ §3 (П4): `pay=1` из адреса при открытии НЕ стирается (повторный вход по той же
    // ссылке снова открывает оплату) — его стирает только успешная оплата (dropPayParam). Ссылка
    // на саму «Подписку» открывает страницу и сразу экран оплаты над ней (просьбу исполняет
    // страница, второго окна подписки не открываем); на других страницах — окно подписки и
    // экран оплаты над ним.
    if (/\/app\/account\/subscription\/?$/.test(url.pathname)) requestAutoPay();
    else openBilling({ pay: true });
  }, [native, openBilling]);

  return null;
}

/** Значок «Оплаты и тарифа» — звезда (строка настроек и пункт второй колонки). */
export const BillingIcon = () => (
  <svg viewBox="0 0 24 24" {...acctIcon}>
    <path d="M12 3l2.6 6.3L21 9.8l-5 4.3 1.6 6.4L12 17l-5.6 3.5L8 14.1 3 9.8l6.4-.5z" />
  </svg>
);

// Строки BillingRow тут больше нет (владелец 2026-08-07): «Подписка» стала страницей
// раздела «Аккаунт», её строку рисует общий список свода (settings.tsx).

// ─────────────────────────── единая модалка ───────────────────────────

// Заголовок и пояснение секции оплаты по состоянию матрицы.

/** Тело модалки оплаты. `inline` — та же страница НА МЕСТЕ списка настроек (вторая колонка десктопа). Слои
 *  пути оплаты от него больше не зависят (PAY-FLOW-TZ §14.6): каждый вертикальный лист — «×», возврат на
 *  «Тариф» — закрытием листа, а не «назад» с переоткрытием модалки. Проп остаётся ради вызовов. */
export function BillingPanel(_props: { inline?: boolean }) {
  const t = useTranslations('cabinet.billing');
  const fmtDate = useFmtDate();
  const fmtMoney = useFmtMoney();
  const drawer = useSlidePanel();
  const native = useNativeApp();
  const { data, reload } = useSummary();
  const locale = useLocale();
  // Строка оферты под кнопкой экрана подтверждения (§18.1 п.5).
  const ofertaLine = useOfertaLine();

  const [busy, setBusy] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);

  // Лист результата оплаты (один экран для всех путей, владелец 09.10.2026) и лист окна кассы
  // (десктоп: iframe Робокассы НА МЕСТЕ листа оплаты, не в поле страницы — ROBOKASSA-SHEET-TZ).
  const openResult = usePayResultSheet();
  const openFrame = usePayFrameSheet();
  const openTopup = useTopupSheet();
  // Возврат с кассы: номер пойман верхом кабинета (или здесь, если «Тариф» смонтировался раньше) —
  // лист результата встаёт стопкой над «Тарифом». Один раз на загрузку (consumePayReturn).
  useEffect(() => {
    if (native) return;
    const ret = consumePayReturn();
    if (ret) openResult({ id: ret.id, wait: 'return', failedHint: !ret.id && ret.fail });
  }, [native, openResult]);
  // Оплата упёрлась в гейт телефона (subscribe → 403 phone_required): не тупик, а шаг
  // подтверждения номера; после него `retry` снова открывает экран оплаты (тот же путь).
  const [phoneStep, setPhoneStep] = useState<{ retry: () => void } | null>(null);
  // Выбранный период: null = «ещё не трогал» → по умолчанию текущий период подписки
  // (данные приходят асинхронно, поэтому дефолт вычисляем, а не кладём в state).
  const [periodSel, setPeriodSel] = useState<Period | null>(null);
  // Момент открытия модалки — достаточно для матрицы (модалка короткоживущая); лениво,
  // чтобы не дёргать Date.now() в теле рендера (чистота рендера).
  const [now] = useState(() => Date.now());

  const sub = data?.subscription ?? null;
  // Рубильник предложения «Pro за видео» (PERSONAL-CHANNELS Л1, владелец 2026-08-26):
  // выключено — ни ссылки «Как получить Pro бесплатно?», ни способа «ролик» внутри неё.
  // Поля нет (старый персистентный кэш) — считаем выключенным: дефолт предложения «выкл».
  const creatorOffer = !!data?.creatorOffer;
  const period: Period =
    periodSel ?? (sub && PERIODS.includes(sub.period as Period) ? (sub.period as Period) : 'month');
  // Цены — в валюте юзера (Э5.1): ₽ / € / $ по ценам версии, без пересчёта по курсу; цену числа
  // аккаунтов считает экран расчёта (`periodPrice`, та же формула, что у сервера).
  const terms = payTerms(data?.terms, data?.currency, data?.discount);
  const currency: Currency = terms?.currency ?? 'RUB';
  const discNow = useDiscountNow(terms);
  const discount = useDiscountText(terms, discNow);
  const info = billingState(
    sub
      ? {
          status: sub.status,
          periodEnd: sub.periodEnd,
          cardBound: !!sub.cardBound,
          paidAccountsCount: sub.paidAccountsCount,
        }
      : null,
    now,
  );

  // Оплата токенами (5.5): доступно без пробных (null = тумблер выключен/старый кэш);
  // превью списания — то же правило, что на сервере: min(доступно, цена). Только при рублях
  // (Э5.2): в € и $ строки «Оплатить токенами» нет.
  const payableTokens = tokensPayAllowed(currency) ? (data?.tokensPay?.payableRub ?? 0) : 0;
  // «Привязанной картой» — только если касса карты совпадает с кассой валюты (Р3): карта
  // Робокассы к платежу в € и $ не подходит, и шторка её не предлагает.
  const savedOk = savedCardUsable(!!info.cardBound, sub?.cardProvider, currency);

  // ПРЕДЗАГРУЗКА (О8): Pro на странице — котировка докупки на 1 аккаунт греется сразу, чтобы
  // «Добавить аккаунты» открылся уже с числами. Сводку грузит сама страница (useSummary).
  const proNow = (sub?.paidAccountsCount ?? 0) > 0 && info.isCurrent;
  useEffect(() => {
    if (!native && proNow) warmTarget('billing.add-quote', addQuoteKey(1), addQuoteUrl(1), { accept: okAccept });
  }, [native, proNow]);
  const stripePk = stripeKeyOf(data);
  // STRIPE.JS — ЗАРАНЕЕ, В ПРОСТОЕ СТРАНИЦЫ «ПОДПИСКА» (€ и $): исполнение скрипта js.stripe.com — 190–220 мс
  // главного потока (профиль long-animation-frame 08.10: кадр 219 мс, блокировка 168, на +254 мс после нажатия).
  // Раньше прогрев шёл в момент открытия экрана расчёта и ложился длинным кадром на анимацию листа (следы §11.3);
  // теперь к нажатию «Перейти на Pro», «Продлить Pro», «Добавить аккаунты» или «Добавить карту» скрипт уже загружен,
  // а их вызовы `stripeFor` берут готовый экземпляр. В приложении — никогда (замок Э5.5 в stripeFor).
  const warmStripe = currency !== 'RUB' && !!stripePk;
  useEffect(() => {
    if (native || !warmStripe || !stripePk) return;
    const w = window as Window & { requestIdleCallback?: (cb: () => void, o?: { timeout: number }) => number; cancelIdleCallback?: (id: number) => void };
    if (w.requestIdleCallback) {
      const id = w.requestIdleCallback(() => void stripeFor(stripePk), { timeout: 3000 });
      return () => w.cancelIdleCallback?.(id);
    }
    const id = window.setTimeout(() => void stripeFor(stripePk), 1500);
    return () => window.clearTimeout(id);
  }, [native, warmStripe, stripePk]);
  // Платёжные данные (сводка плательщика слоя «Оплата», PAY-SCREEN-TZ §2.2) — тоже заранее,
  // вместе со сводкой подписки: слой открывается уже с ними.
  useEffect(() => {
    if (!native) warmTarget('billing.payer', PAYER_KEY, PAYER_URL, { accept: okAccept });
  }, [native]);

  // ─────────── СЛОЙ «ОПЛАТА» ПРЯМО С ЭКРАНА РАСЧЁТА (PAY-SCREEN-2-TZ §1.1, §4) ───────────
  // Шторки подтверждения нет: кнопка расчёта открывает слой «Оплата» стопкой над расчётом
  // («‹» — назад, состояние расчёта сохраняется). Платёж создаёт кнопка СЛОЯ (`post`). Ключ
  // Stripe.js и кошельки — из сводки (§2.1, §3.2): для открытия слоя сервер не нужен.
  const payWallets = parseWallets(data?.payWallets);
  // Строка «Карта» слоя (§1.2 п.4, §4): подпись привязанной карты — как на «Подписке» («Visa ··4242»
  // у Stripe); у Робокассы бренда нет — «Сохранённая карта». Касса карты ≠ касса валюты — строки нет.
  const savedCardLabel = savedOk ? cardDisplay(sub?.cardLabel, t('payCardPick'), t('cardSection')) : null;

  /** Отказ ручки на слое → текст у его кнопки (§2.2 п.3); нет телефона — шаг подтверждения на
   *  «Подписке», после него — снова экран оплаты (`retry`). */
  const layerErrorText = (code: string, closeKey: PayCloseKey, retry: () => void): string | null => {
    if (code === 'phone_required') {
      drawer.closeFrom([closeKey]);
      setPhoneStep({ retry });
      return null;
    }
    // Число аккаунтов или токены устарели, пока слой был открыт — сводка освежается.
    if (code === 'accounts_locked' || code === 'tokens_insufficient') reload();
    return t(payErrorKey(code));
  };

  /** Исход ручки не про слой (dev без кассы, Робокасса) — на «Подписке», как было. Касса (`redirect`) — лист НЕ
   *  закрывается до ухода (§18.1 п.9: «вместо этого на кнопке спиннер … пока она не открылась»): телефон — сразу
   *  переход, спиннер крутится до ухода страницы; широкий экран — лист кассы встаёт НОВЫМ уровнем поверх листа
   *  подтверждения (ROBOKASSA-SHEET-TZ §3.1), кнопка под ним снова живая (true) — «×» кассы возвращает на него;
   *  результат снимает весь путь от `closeKey`. Остальные исходы — лист снимается, как было. Ответ — для кнопки
   *  листа: 'away' — спиннер остаётся. */
  const settleOutside = (step: PayStep, closeKey: PayCloseKey, retry: () => void): CheckGo => {
    if (step.kind === 'redirect') {
      if (payViaRedirect()) {
        window.location.assign(step.url); // мобильные: СБП не работает в iframe
        return 'away';
      }
      openFrame({ url: step.url, id: step.paymentId, closeKeys: [closeKey], onPaid: reload, onRetry: retry });
      return true;
    }
    // Отказ ручки (§3.4 п.3): `phone_required` снимает путь сам (layerErrorText). Второй closeFrom того же имени уже
    // не находит уровень и снимал «Тариф» под ним (closeLevel) — на телефоне выкидывало в «Аккаунт», шаг номера терялся.
    if (step.kind === 'error') {
      const text = layerErrorText(step.error, closeKey, retry);
      if (text !== null) {
        drawer.closeFrom([closeKey]);
        setErr(text);
      }
      return true;
    }
    drawer.closeFrom([closeKey]);
    if (step.kind === 'done') {
      // dev без кассы либо полная оплата токенами — подписка уже исполнена: экран результата;
      // сводка «Тарифа» освежается данными, страница не перезагружается.
      reload();
      if (step.paymentId) openResult({ id: step.paymentId, onRetry: retry });
      return true;
    }
    // Списание привязанной картой принято — экран результата с ожиданием (без формы).
    if (step.kind === 'charging') {
      openResult({ id: step.paymentId, wait: 'charging', onRetry: retry });
      return true;
    }
    setErr(t('errGeneric'));
    return true;
  };

  /**
   * СЛОЙ «ОПЛАТА» € И $ (§1–§2): открывается сразу, без сервера — с ключом Stripe.js из сводки и
   * платёжными данными (прогреты вместе со сводкой). Нет ключа (касса Stripe недоступна) или
   * данных — слоя нет, false: экран расчёта показывает общий текст ошибки.
   */
  const openStripeLayer = async (o: {
    amount: number;
    currency: Currency;
    /** Автопродление и «Сохранить карту» с экрана подтверждения (§18.2): PayPal в ряду только без автопродления. */
    autorenew: boolean;
    bindCard: boolean;
    /** Следующее списание — для Apple Pay (§3.6). */
    next: PayNext;
    closeKey: PayCloseKey;
    post: (body: LayerPayBody) => Promise<Record<string, unknown>>;
    retry: () => void;
  }): Promise<boolean> => {
    if (isNativeApp() || !stripePk) return false; // замок Э5.5: в приложении оплаты нет
    const payer = await awaitBilling(PAYER_KEY, PAYER_URL, parsePayerResponse);
    if (!payer) return false;
    const lock = cardSheetLock();
    drawer.open({
      title: o.closeKey === 'pay-add' ? t('addAccounts') : proNow ? t('goRenew') : t('payLayerTitle'),
      sheet: true,
      lead: 'close', // вертикальный лист пути оплаты — «×» (PAY-FLOW-TZ §14.6, канон М83)
      winKey: 'pay-card',
      // Пока идёт платёж (в т.ч. окно 3-D Secure и лист кошелька) — «‹» и Escape слой не снимают.
      closeAsk: () => Promise.resolve(!lock.busy()),
      body: (
        <StripeCardSheet
          publishableKey={stripePk}
          amount={o.amount}
          currency={o.currency}
          priceLabel={fmtMoney(o.amount, o.currency)}
          wallets={payWallets}
          autorenew={o.autorenew}
          bindCard={o.bindCard}
          next={o.next}
          payer={payer}
          lock={lock}
          run={async (body) => payStep(await o.post(body))}
          errorText={(code) => layerErrorText(code, o.closeKey, o.retry)}
          onOther={(step) => settleOutside(step, o.closeKey, o.retry)}
          onSubmitted={(id, wait) => {
            drawer.closeFrom([o.closeKey]);
            openResult({ id, wait: wait === 'confirming' ? 'confirming' : 'charging', onRetry: o.retry });
          }}
        />
      ),
    });
    return true;
  };

  /** ЭКРАН 2 — «ПОДТВЕРЖДЕНИЕ», ОДИН НА РУБЛИ И ЕВРО (PAY-FLOW-TZ §18.2): «К оплате», «Автопродление», «Карта»
   *  (если сохранена), «Сохранить карту», «Плательщик», прибитая кнопка с подписями. Рубли и «всё токенами» —
   *  нажатие сразу создаёт платёж (касса или исполнение); евро — новая карта открывает форму карты стопкой,
   *  сохранённая — списание сразу (Stripe.js подтверждает PaymentIntent, 3-D Secure — его окном). На всю высоту,
   *  «×». Открывается с платёжными данными; не пришли — false (общий текст ошибки). */
  const openPayCheck = async (o: {
    title: string;
    amount: string;
    what: string;
    /** Сумма деньгами числом — для формы карты (евро). */
    cash: number;
    priceLabel: string;
    next: PayNext;
    currency: Currency;
    autorenewRow: boolean;
    autorenewStart: boolean;
    onAutorenew: (on: boolean) => void;
    tokensAll: boolean;
    closeKey: PayCloseKey;
    post: (body: CheckPayBody | LayerPayBody) => Promise<Record<string, unknown>>;
    retry: () => void;
  }): Promise<boolean> => {
    if (isNativeApp()) return false;
    const rub = o.currency === 'RUB';
    if (!o.tokensAll) {
      if (!rub && !stripePk) return false;
      const payer = await awaitBilling(PAYER_KEY, PAYER_URL, parsePayerResponse);
      if (!payer) return false;
    }
    const date = fmtDate(o.next.date.toISOString());
    const c: Check = {
      amount: o.amount,
      what: o.what,
      rub,
      autorenewRow: o.autorenewRow,
      autorenewStart: o.autorenewStart,
      onAutorenew: o.onAutorenew,
      savedCard: savedCardLabel,
      tokensAll: o.tokensAll,
      priceLabel: o.priceLabel,
      footOn: t('checkFootOn', { now: o.amount, price: fmtMoney(o.next.amount, o.currency), period: o.next.period, date }),
      footOff: t('checkFootOff', { now: o.amount, date }),
      oferta: ofertaLine,
      go: async (body) => {
        if (rub || o.tokensAll) return settleOutside(payStep(await o.post(body)), o.closeKey, o.retry);
        if (!body.saved) {
          return openStripeLayer({
            amount: o.cash,
            currency: o.currency,
            autorenew: body.autorenew === true,
            bindCard: body.bindCard,
            next: o.next,
            closeKey: o.closeKey,
            post: o.post,
            retry: o.retry,
          });
        }
        // Сохранённая карта Stripe — списание сразу (§18.2): PaymentIntent с картой создал сервер, подтверждение —
        // Stripe.js; принят — экран результата с ожиданием, как у рублёвой сохранённой карты.
        const step = payStep(await o.post(body));
        if (step.kind !== 'stripe-saved') return settleOutside(step, o.closeKey, o.retry);
        const res = await confirmSavedStripeCard(step.publishableKey, step.clientSecret, stripeReturnUrl(window.location.origin, locale, step.paymentId));
        if (!res.ok) {
          stripeErrorForClient(res.error, t('errGeneric'), { paymentId: step.paymentId, step: 'confirmSaved' });
          return false;
        }
        drawer.closeFrom([o.closeKey]);
        openResult({ id: step.paymentId, wait: 'charging', onRetry: o.retry });
        return true;
      },
    };
    drawer.open({
      title: o.title,
      sheet: true,
      lead: 'close', // вертикальный лист пути оплаты — «×» (PAY-FLOW-TZ §14.6, канон М83)
      // С витрины (§3.4) подтверждение — своим именем уровня: «×» и ошибки снимают только его, витрина под ним.
      ...(o.closeKey === 'pay-check' ? { winKey: 'pay-check' } : { winKey: 'pay-confirm' }),
      body: <CheckSheet c={c} />,
    });
    return true;
  };

  // «Остаться на бесплатном» — слой с условием: подписаться на нас и отметить это.
  // «Добавить аккаунты» на Pro — доплата до конца оплаченного периода (Э1.4).
  // Слой докупки открывается только с котировкой на 1 аккаунт (О8): она греется заранее при
  // открытии «Подписки» (эффект ниже); не успела — кнопка ждёт её, а не открывает пустой слой.
  /** `pre` — докупка из подключения сверх оплаченных (ТЗ ACCOUNTS-TOGGLE §3.3): число и строка сверху. */
  const openAddAccounts = async (pre?: AddAccountsRequest) => {
    if (busy !== null) {
      pre?.onClosed(false);
      return;
    }
    const firstN = pre?.want ?? 1;
    setBusy('add');
    const q = await awaitBilling(addQuoteKey(firstN), addQuoteUrl(firstN), parseAddQuote);
    setBusy(null);
    if (!q) {
      setErr(t('errGeneric'));
      pre?.onClosed(false);
      return;
    }
    // Stripe.js — заранее, на экране расчёта (§1.4 п.3): слой встанет с живыми полями быстрее.
    if (q.currency !== 'RUB' && stripePk) void stripeFor(stripePk);
    drawer.open({
      title: t('addAccounts'),
      ...webOnly({ sheet: true }), // оплата — вертикальный лист в браузере (ТЗ WEB-LOOK Э2.5)
      lead: 'close', // вертикальный лист пути оплаты — «×» (PAY-FLOW-TZ §14.6, канон М83)
      winKey: 'pay-add',
      body: (
        <AddAccounts
          {...(pre ? { startWant: pre.want, head: t('needPayTitle', { paid: pre.paid, need: pre.need }), onClosed: pre.onClosed } : {})}
          terms={terms}
          startAutorenew={autorenewStart('add', terms?.currency ?? 'RUB', sub?.autorenew)}
          onPay={(n, price, cur, next, until, auto) => {
            const post = (body: LayerPayBody | CheckPayBody) =>
              postJson('/api/billing/account-add', { accounts: n, ...body });
            const retry = () => void openAddAccounts(pre ? { ...pre, onClosed: () => undefined } : undefined);
            const shared = {
              title: t('addAccounts'),
              amount: fmtMoney(price, cur),
              what: t('checkWhatAdd', { n, date: fmtDate(until) }),
              currency: cur,
              next,
              autorenewRow: true,
              autorenewStart: auto.value,
              onAutorenew: auto.set,
            };
            // Экран подтверждения — один на рубли и евро (§18.2); не открылся — общий текст на «Тарифе».
            void openPayCheck({ ...shared, cash: price, priceLabel: fmtMoney(price, cur), tokensAll: false, closeKey: 'pay-add', post, retry }).then(
              (ok) => !ok && setErr(t('errGeneric')),
            );
          }}
        />
      ),
    });
  };


  /**
   * ПОДТВЕРЖДЕНИЕ ОПЛАТЫ ПОДПИСКИ — ОДИН ВХОД ДЛЯ ВИТРИНЫ И ЭКРАНА РАСЧЁТА (TARIFF-PRO-2-TZ §3.4). Считает сам
   * (`payCalc`: цена с личной скидкой, токены по максимуму, остаток деньгами, следующее списание) и открывает экран
   * подтверждения. Витрина (первая покупка) зовёт её напрямую — экрана расчёта на этом пути нет, `closeKey`
   * 'pay-check'; экран расчёта Pro — со своим выбором и своим переключателем автопродления, `closeKey` 'pay-screen'.
   */
  const startPayCheck = (o: {
    period: Period;
    accounts: number;
    useTokens: boolean;
    action: PayAction;
    auto: AutoChoice;
    closeKey: 'pay-screen' | 'pay-check';
    retry: () => void;
  }) => {
    const { tokensUsed, cash, next } = payCalc({
      terms,
      period: o.period,
      accounts: o.accounts,
      payableTokens,
      useTokens: o.useTokens,
      action: o.action,
      periodEnd: sub?.periodEnd ?? null,
      now: new Date(),
    });
    const post = (body: LayerPayBody | CheckPayBody) =>
      postJson('/api/billing/subscribe', { period: o.period, accounts: o.accounts, ...body, useTokens: o.useTokens });
    // Всё токенами (§2.1): экрана оплаты нет — платёж сразу из полушторки; карты нет — строки
    // «Автопродление» нет и `autorenew: null`.
    const tokensAll = cash <= 0;
    const shared = {
      title: proNow ? t('goRenew') : t('payScreenTitle'),
      // При токенах (рубли): сумма — деньгами, сноска добавляет списание с баланса (§14.3 п.1);
      // всё токенами — сумма токенами («1 500 токенов»), не «0 ₽» (приёмка 09.10).
      amount: tokensAll ? t('tokens', { n: tokensUsed }) : fmtMoney(cash, currency),
      what:
        t(o.period === 'year' ? 'checkWhatYear' : 'checkWhatMonth', { n: o.accounts }) +
        (tokensUsed > 0 && !tokensAll ? ` · ${t('checkTokensMore', { n: tokensUsed })}` : ''),
      currency,
      next,
      autorenewRow: autorenewRowShown(cash, savedOk),
      autorenewStart: o.auto.value,
      onAutorenew: o.auto.set,
    };
    // Экран подтверждения — один на рубли и евро (§18.2); всё токенами — платёж сразу с того же листа.
    void openPayCheck({ ...shared, cash, priceLabel: fmtMoney(cash, currency), tokensAll, closeKey: o.closeKey, post, retry: o.retry }).then(
      (ok) => !ok && setErr(t('errGeneric')),
    );
  };

  /** ВИТРИНА → СРАЗУ ПОДТВЕРЖДЕНИЕ (§3.4 п.2): выбор витрины, автопродление по правилу первой оплаты (рубли — как у
   *  подписки, евро/доллары — включено); его положение помнит повтор (телефон, «Повторить» на результате). */
  const payFromShowcase = (period: Period, accounts: number, useTokens: boolean) => {
    if (!payScreenReady(data)) return;
    // Stripe.js (€ и $) — греется ещё при показе витрины (эффект warmStripe выше); здесь — готовый экземпляр.
    if (currency !== 'RUB' && stripePk) void stripeFor(stripePk);
    const box: AutoChoice = {
      value: autorenewStart('first', currency, sub?.autorenew),
      set: (on) => {
        box.value = on;
      },
    };
    const run = () =>
      startPayCheck({ period, accounts, useTokens, action: 'first', auto: box, closeKey: 'pay-check', retry: run });
    run();
  };

  // ЭКРАН ОПЛАТЫ отдельным слоем (владелец 2026-08-10): на странице тарифа выбора нет,
  // выбор числа аккаунтов, срока и способа живёт здесь.
  /** `autorenewOn` — открыть с включённым автопродлением (шторка «Автопродление» в рублях без карты,
   *  PAY-FLOW-TZ §6.3: «Продлить Pro» с включённым автопродлением). */
  /** `pre` — повтор неудачной оплаты с экрана результата: тот же срок и то же число аккаунтов. */
  const openPayScreen = (autorenewOn?: boolean, pre?: { period: Period | null; accounts: number | null }) => {
    // Слой оплаты — только со сводкой и условиями цен (О8): кнопка до них погашена.
    if (!payScreenReady(data)) return;
    // Stripe.js — при открытии ЭКРАНА РАСЧЁТА, не слоя (§1.4 п.3).
    if (currency !== 'RUB' && stripePk) void stripeFor(stripePk);
    drawer.open({
      // Заголовок — по действию (владелец 08.10): продление — «Продлить Pro», первая оплата — «Оплата».
      title: proNow ? t('goRenew') : t('payScreenTitle'),
      ...webOnly({ sheet: true }), // оплата — вертикальный лист в браузере (ТЗ WEB-LOOK Э2.5)
      lead: 'close', // вертикальный лист пути оплаты — «×» (PAY-FLOW-TZ §14.6, канон М83)
      // Имя уровня — чтобы оплата сняла экран оплаты со всем, что над ним («Оплата»), одним
      // ходом (closeFrom).
      winKey: 'pay-screen',
      body: (
        <PayScreen
          suggested={Math.max(1, pre?.accounts ?? data?.accounts?.suggested ?? 1)}
          connected={data?.accounts?.connected ?? data?.accounts?.inUse ?? 0}
          startPeriod={pre?.period ?? period}
          terms={terms}
          payableTokens={payableTokens}
          isCurrent={info.isCurrent}
          paidCount={sub?.paidAccountsCount ?? 0}
          periodEnd={sub?.periodEnd ?? null}
          startAutorenew={autorenewOn ? true : autorenewStart(proNow ? 'renew' : 'first', currency, sub?.autorenew)}
          onPay={(p, acc, tokens, auto) => {
            setPeriodSel(p);
            startPayCheck({
              period: p,
              accounts: acc,
              useTokens: tokens,
              action: proNow ? 'renew' : 'first',
              auto,
              closeKey: 'pay-screen',
              retry: () => openPayScreen(autorenewOn),
            });
          }}
        />
      ),
    });
  };

  // УПРАВЛЕНИЕ АВТОПРОДЛЕНИЕМ И КАРТОЙ (PAY-FLOW-TZ §6): строки «Автопродление» и «Карта» и их
  // шторки — общим хуком (его же зовёт страница «Тариф» приложения, без сумм, §5).
  const manage = useManageSheets({
    onError: (text) => setErr(text),
    // Ветку «нет карты» выбирает хук по `subscription.cardSetup` (§7.5); слой — в валюте подписки.
    onAddCard: () => void openAddCard(),
    onRenewAuto: () => openPayScreen(true),
    onBusy: (b) => setBusy(b ? 'autorenew' : null),
  });

  /** СЛОЙ «ДОБАВИТЬ КАРТУ» (§6.5): сохранение карты без оплаты стопкой над «Подпиской»; после
   *  «Сохранить карту» — «Проверяем карту…» на странице, пока вебхук не запишет карту (§7.4). */
  const openAddCard = async () => {
    if (isNativeApp() || !stripePk || !sub) return setErr(t('errGeneric'));
    const payer = await awaitBilling(PAYER_KEY, PAYER_URL, parsePayerResponse);
    if (!payer) return setErr(t('errGeneric'));
    const next = manage.next;
    const lock = cardSheetLock();
    drawer.open({
      title: t('addCardTitle'),
      sheet: true,
      lead: 'close', // вертикальный лист пути оплаты — «×» (PAY-FLOW-TZ §14.6, канон М83)
      winKey: 'pay-card-setup',
      closeAsk: () => Promise.resolve(!lock.busy()),
      body: (
        <AddCardSheet
          publishableKey={stripePk}
          currency={manage.currency}
          wallets={payWallets}
          next={next}
          note={manage.addCardNote}
          payer={payer}
          lock={lock}
          onSaved={(id) => {
            drawer.closeFrom(['pay-card-setup']);
            setCardWait(id);
          }}
        />
      ),
    });
  };
  // Ожидание записи карты вебхуком (§6.5): номер SetupIntent; null — не ждём.
  const [cardWait, setCardWait] = useState<string | null>(null);

  // «Как получить Pro бесплатно» — отдельный слой с двумя способами (ролик за месяц
  // и партнёрская ссылка). Заказ владельца 2026-08-09. Способ «ролик» показываем
  // только при включённом предложении «Pro за видео» (рубильник админки, Л1).
  const openFreePro = () => {
    drawer.open({
      title: t('freeProTitle'),
      sheet: true,
      lead: 'close', // вертикальный лист пути оплаты — «×» (PAY-FLOW-TZ §14.6, канон М83)
      body: (
        // Два способа — разделами списка: подпись способа над текстом (М57).
        <FPane>
          {creatorOffer && (
            <KListSection label={t('freeProReelHead')}>
              <KListNote>{t('freeProReel')}</KListNote>
            </KListSection>
          )}
          <KListSection label={t('freeProPartnerHead')}>
            <KListNote>{t('freeProPartner')}</KListNote>
          </KListSection>
        </FPane>
      ),
    });
  };

  // «Повторить оплату» на экране результата (лист возврата с кассы): тот же экран оплаты с теми же
  // параметрами — подписка (срок, число аккаунтов), докупка, пополнение баланса AI. Регистрируется,
  // пока «Тариф» открыт; обработчик — свежий на каждый рендер (ref), без переподписки.
  const retryRef = useRef<(d: PayResultData) => void>(() => {});
  const retryNow = (d: PayResultData) => {
    if (d.kind === 'account_add') return void openAddAccounts();
    if (d.kind === 'balance_topup') {
      openTopup();
      return;
    }
    // Без Pro экрана расчёта нет (TARIFF-PRO-2-TZ §3.4): повтор первой покупки — сразу подтверждение с тем же выбором.
    if (!proNow) {
      payFromShowcase(d.period ?? 'year', Math.max(1, d.accounts ?? data?.accounts?.suggested ?? 1), false);
      return;
    }
    openPayScreen(undefined, { period: d.period, accounts: d.accounts });
  };
  useEffect(() => {
    retryRef.current = retryNow;
  });
  useEffect(() => {
    if (native) return;
    return setPayResultRetry((d) => retryRef.current(d));
  }, [native]);

  // ССЫЛКА ?pay=1 (FUNNEL-TZ §3): просьбу верха кабинета исполняет «Подписка», как только пришла
  // сводка с условиями цен (экран оплаты без них не открывается) — тем же openPayScreen, что кнопка.
  // Без Pro (бесплатный, пробный, просрочка) просьба просто снимается: «Тариф» — сама витрина Pro, она и есть экран
  // оплаты, поверх ничего не открывается (TARIFF-PRO-2-TZ §3.4 п.5).
  const [autoPayTick, setAutoPayTick] = useState(0);
  useEffect(() => {
    const fn = () => setAutoPayTick((n) => n + 1);
    autoPaySubs.add(fn);
    return () => {
      autoPaySubs.delete(fn);
    };
  }, []);
  const openPayRef = useRef(openPayScreen);
  useEffect(() => {
    openPayRef.current = openPayScreen;
  });
  const payReady = payScreenReady(data);
  useEffect(() => {
    if (native || !payReady) return;
    if (takeAutoPay() && proNow) openPayRef.current();
  }, [native, payReady, autoPayTick, proNow]);
  // ДОКУПКА ИЗ ПОДКЛЮЧЕНИЯ (ТЗ ACCOUNTS-TOGGLE §3.3): просьбу исполняет «Подписка», как только
  // пришла сводка, — тем же openAddAccounts, что кнопка «Добавить аккаунты».
  const [addTick, setAddTick] = useState(0);
  useEffect(() => {
    const fn = () => setAddTick((n) => n + 1);
    addSubs.add(fn);
    return () => {
      addSubs.delete(fn);
    };
  }, []);
  const openAddRef = useRef(openAddAccounts);
  useEffect(() => {
    openAddRef.current = openAddAccounts;
  });
  useEffect(() => {
    if (native || !payReady) return;
    const req = takeAddAccounts();
    if (req) void openAddRef.current(req);
  }, [native, payReady, addTick]);

  if (phoneStep) {
    return (
      <PhoneVerifyStep
        onVerified={() => {
          setPhoneStep(null);
          phoneStep.retry(); // телефон подтверждён — снова экран оплаты
        }}
        onCancel={() => {
          setPhoneStep(null);
          setBusy(null);
        }}
      />
    );
  }

  if (cardWait) {
    return (
      <CardSetupWait
        setupId={cardWait}
        onDone={() => {
          setCardWait(null);
          reload();
        }}
      />
    );
  }

  // ВЕРХ СТРАНИЦЫ — «якорь» биллинг-страницы по канону SaaS-биллинга (current plan
  // summary): имя тарифа + строки «ключ — значение» (аккаунты · списание · подпись).
  // Никаких абзацев: владелец 2026-08-09 — «четыре абзаца текста на странице оплаты» нет.
  const paidCount = sub?.paidAccountsCount ?? 0;
  const isPro = paidCount > 0 && info.isCurrent;
  const planName = isPro ? t('planPro') : t('planFree');
  // Сколько дней осталось от пробного периода (null — его уже нет).
  const trialLeft = (() => {
    if (!data?.trialUntil) return null;
    const left = Math.ceil((new Date(data.trialUntil).getTime() - now) / 86_400_000);
    return left > 0 ? left : null;
  })();
  // ЧЕТЫРЕ СОСТОЯНИЯ СТРАНИЦЫ (владелец 18.09.2026: «два режима — платный и бесплатный»,
  // прежних «подпишитесь за N дней» и «тариф снят, боты выключены» больше нет):
  //   trial    — идёт пробный период, всё открыто, показываем остаток дней;
  //   free     — пробный кончился, не платит: работают FREE_ACCOUNTS аккаунтов, в
  //              сообщениях бота наша строка; предлагаем Pro;
  //   pro      — оплачено: срок и предложение продлить;
  //   overdue  — платил раньше, срок вышел: работают только первые FREE_ACCOUNTS аккаунтов.
  // ПОКА СВОДКА НЕ ПРИШЛА — состояния НЕТ (владелец 2026-08-10 поймал на скриншоте:
  // страница успевала показать «вы на бесплатном» раньше данных и врала). Скелетон.
  // Вид — из `summary.plan` (FUNNEL-TZ §4.2, П4; одна формула тарифа на сервере), у старого
  // ответа без поля — прежнее правило (lib/billing-view.ts).
  const view = billingViewOf(data, { isPro, trialLeft });

  // «Списание»: у Pro — дата и сумма продления либо «вручную»; у Free — прочерк.
  const nextCharge = !isPro ? '—' : sub?.periodEnd ? fmtDate(sub.periodEnd) : '—';

  // СТРАНИЦА ПО КАНОНУ APPLE (ТЗ WEB-CAUSES М57, владелец 05.10 21:50; образец — «Уведомления»/
  // «Безопасность», кадры iOS K04/K06): герой (значок · имя тарифа · подводка) → разделы списка
  // набора (подпись над группой, строки «ключ — значение», сноска под группой 13 серым) → кнопки
  // роли «действие» (М33) во всю ширину колонки, одной ширины. Своих классов страницы нет.
  const actionsHidden = view === 'loading' ? { style: { visibility: 'hidden' as const } } : {};
  // ВИТРИНА PRO вместо условий бесплатного (владелец 12.10.2026, TARIFF-PRO-TZ §1): бесплатный, пробный,
  // просрочка. Pro и загрузка — прежняя страница.
  const showcase = !!data && (view === 'free' || view === 'trial' || view === 'overdue');
  const discountRow = discount ? (
    <KListSection>
      <KListGroup>
        <KListRow className="bill-discount" title={discount.title} hint={discount.left ?? undefined} />
      </KListGroup>
    </KListSection>
  ) : null;
  const freeProRow =
    creatorOffer && (view === 'free' || view === 'overdue') ? (
      <KListGroup>
        <KListRow title={t('freeProLink')} tone="accent" chevron onClick={openFreePro} />
      </KListGroup>
    ) : null;
  return (
    <>
      {/* ГЕРОЙ СТРАНИЦЫ рисует сама панель (владелец 2026-08-09): под иконкой — имя
          ТАРИФА клиента и его короткое описание, а не служебное слово «Подписка».
          Поэтому свод не рисует свой герой для этой страницы (settings.tsx: noHero). */}
      <SettingsHero
        icon={<BillingIcon />}
        // Телефон в браузере (ТЗ WEB-PARITY-3 К6, решение владельца В-К3): заголовок «Тариф»,
        // платёжное содержимое страницы остаётся вебовским.
        // ДО ДАННЫХ — КАРКАС ГЕРОЯ ТОЙ ЖЕ ВЫСОТЫ (правило 1 владельца 10.10.2026, PREFETCH-TZ §2.4): невидимые
        // слово и подводка (sk-hold) держат место, а не пустой герой, который вырастает с приходом сводки.
        // ВИТРИНА PRO (TARIFF-PRO-TZ §1 п.1): на бесплатном, пробном и в просрочке герой — «Scriptera Pro» и
        // подводка про все возможности, на всех ширинах; у Pro — как было.
        title={
          !data ? (
            <span className="sk-hold">{t('planLabel')}</span>
          ) : showcase ? (
            t('showcase.title')
          ) : readPhoneLook() ? (
            t('planLabel')
          ) : (
            planName
          )
        }
        meta={
          !data ? (
            <span className="sk-hold">{t('about.free', { n: FREE_ACCOUNTS })}</span>
          ) : showcase ? (
            t('showcase.lead')
          ) : (
            t(`about.${view}`, { n: FREE_ACCOUNTS })
          )
        }
        color="var(--amber)"
      />
      {/* Герой — НАД телом формы, как у «Уведомлений» (свод рисует героя перед FPane): воздух до
          первой подписи даёт поле FPane, а не своё число. `bill-page` — добег под прибитым подвалом. */}
      <FPane className="bill-page">
        {/* Ошибка — красным сразу под шапкой (стандарт модалок, владелец 2026-07-23). */}
        {err && <DError>{err}</DError>}

        {/* Пока сводка едет — группа держит место, не рисуясь (Н17: скелетонов-полос нет). */}
        {view === 'loading' && (
          <KListSection label={t('planLabel')}>
            <KListGroup className="sk-hold">
              <KListRow title={<Sk w={150} h={16} />} />
            </KListGroup>
          </KListSection>
        )}

        {showcase && data && (view === 'free' || view === 'trial' || view === 'overdue') ? (
          <ProShowcase
            view={view}
            data={data}
            terms={terms}
            trialLeft={trialLeft}
            disabled={busy !== null}
            onPay={payFromShowcase}
            discount={discount}
            payableTokens={payableTokens}
            tail={
              <>
                {freeProRow}
                {view === 'overdue' && <OverdueSections data={data} keep={FREE_ACCOUNTS} />}
              </>
            }
          />
        ) : (
          <>
        {/* PRO: что оплачено. */}
        {view === 'pro' && (
          <KListSection label={t('planPro')}>
            <KListGroup>
              <KListRow title={t('kvPaidUntil')} value={nextCharge} />
              {/* СТРЕЛОЧКА ВНИЗ У ЧИСЛА АККАУНТОВ (ТЗ USERTEST-1014 п.12, владелец 14.10.2026): тап — шторка «Добавить
                  аккаунты» (та, что открывала убранная кнопка под «Продлить Pro»). */}
              <KListRow
                title={t('accountsLabel')}
                value={
                  <span className="bill-acc-more">
                    {t('accountsN', { n: paidCount })}
                    <svg width="9" height="6" viewBox="0 0 10 6" fill="none" stroke="currentColor" strokeWidth="1.6" aria-hidden>
                      <path d="M1 1l4 4 4-4" />
                    </svg>
                  </span>
                }
                {...(busy === null ? { onClick: () => void openAddAccounts() } : {})}
              />
            </KListGroup>
          </KListSection>
        )}
        {/* АВТОПРОДЛЕНИЕ И КАРТА (PAY-FLOW-TZ §6.1) — строками-переходами «›» под разделом Pro:
            «Автопродление» на Pro всегда (включить можно и без карты), «Карта» — когда сохранена
            (обе кассы: «Visa ··4242» / «Карта ··1234» / «Сохранённая карта»). */}
        {view === 'pro' && <ManageRows m={manage} />}

        {/* PRO: вместо таблички условий — «Что входит в Pro» (TARIFF-PRO-TZ §4), те же строки и полушторки. */}
        {view === 'pro' && <ProPerks />}

        {/* ЛИЧНАЯ СКИДКА над кнопкой оплаты (PRICES-TZ §6, §11.2 п.3): пока действует — «Скидка N %
            до даты» (и отсчёт в последние 48 ч); истекла — строки нет. В приложении — ничего. */}
        {view !== 'loading' && discountRow}

        {/* КНОПКИ — ПРИБИТЫЙ ПОДВАЛ (владелец 10.10.2026: «кнопку перенести вниз, прибить»): тот же рецепт, что у
            экрана расчёта (CalcFoot). Пробный — «Подключить Pro»; бесплатный/просрочка — «Перейти на Pro»/«Продлить
            Pro»; Pro — «Продлить Pro» (кнопка «Добавить аккаунты» снята, ТЗ USERTEST-1014 п.12: аккаунты добавляют
            стрелочкой у их числа) и под ней «Защищённая оплата…», как под «Подключить Pro». Роль «действие» (М33). */}
        <CalcFoot {...(view === 'pro' ? { note: <CalcSecure currency={currency} /> } : {})}>
          <KBareButton
            className="k-btn--action cta-grad"
            onClick={() => openPayScreen()}
            disabled={!data || busy !== null}
            {...actionsHidden}
          >
            {t(view === 'pro' || view === 'overdue' ? 'goRenew' : view === 'trial' ? 'goConnect' : 'goUpgrade')}
          </KBareButton>
        </CalcFoot>
          </>
        )}
      </FPane>
    </>
  );
}

// ─────────────── автопродление и карта: строки и шторки «Подписки» (PAY-FLOW-TZ §6) ───────────────

/** Что знают строки «Автопродление» / «Карта» и их шторки. `openAutorenew`/`openCard` — null, когда
 *  строка без перехода (в приложении без карты включать автопродление нечем, §5). */
export interface ManageState {
  pro: boolean;
  autorenew: boolean;
  /** Подпись сохранённой карты («Visa ··4242» / «Карта ··1234» / «Сохранённая карта»); null — нет. */
  card: string | null;
  currency: Currency;
  /** Следующее списание по подписке (сводка §7.5, иначе `nextCharge`); null — не посчитать. */
  next: PayNext | null;
  /** Подпись слоя «Добавить карту» (§6.5). */
  addCardNote: string;
  openAutorenew: (() => void) | null;
  openCard: (() => void) | null;
}

/**
 * УПРАВЛЕНИЕ АВТОПРОДЛЕНИЕМ И КАРТОЙ (PAY-FLOW-TZ §6.2–§6.4) — мелкие шторки по канону Apple: сноска и
 * две кнопки роли «действие» одной ширины друг под другом. Сумма и дата — из сводки (`nextChargeAt`/
 * `nextChargeAmount`, договор §7.5), у старого ответа — `nextCharge` той же подписки. В приложении
 * (§5) тексты без сумм и валют, «Добавить карту» и «Продлить Pro» нет (это оплата) — строка
 * «Автопродление» без карты там без перехода. Выключение не трогает карту (§7.1), удаление карты
 * выключает автопродление (§7.3).
 */
export function useManageSheets(o: {
  onBack?: () => void;
  onError: (text: string) => void;
  /** «Добавить карту» (евро и доллары, §6.5); нет — в приложении. */
  onAddCard?: () => void;
  /** «Продлить Pro» с включённым автопродлением (рубли без карты, §6.3). */
  onRenewAuto?: () => void;
  onBusy?: (busy: boolean) => void;
}): ManageState {
  const t = useTranslations('cabinet.billing');
  const fmtDate = useFmtDate();
  const fmtMoney = useFmtMoney();
  const drawer = useSlidePanel();
  const native = useNativeApp();
  const { data, reload } = useSummary();
  const [now] = useState(() => Date.now());
  const sub = data?.subscription ?? null;
  const info = billingState(
    sub
      ? { status: sub.status, periodEnd: sub.periodEnd, cardBound: !!sub.cardBound, paidAccountsCount: sub.paidAccountsCount }
      : null,
    now,
  );
  const paid = sub?.paidAccountsCount ?? 0;
  const pro = paid > 0 && info.isCurrent;
  const autorenew = sub?.autorenew === true;
  const card = sub?.cardBound ? cardDisplay(sub.cardLabel, t('payCardPick'), t('cardSection')) : null;
  const terms = payTerms(data?.terms, data?.currency, data?.discount);
  // Деньги подписки — в её валюте (автосписания идут в ней), не в валюте юзера (приёмка 08.10).
  const currency: Currency = subscriptionCurrency(sub, terms?.currency ?? 'RUB');
  const period: PayPeriod = sub?.period === 'year' ? 'year' : 'month';
  // Сводка не прислала следующее списание (старый ответ) — считаем сами, но только если цены юзера в той же
  // валюте, что подписка: иначе сумму показать нечем (цены другой валюты к подписке не относятся).
  const nc =
    summaryNextCharge(sub) ??
    (terms && terms.currency === currency && sub?.periodEnd
      ? nextCharge({ action: 'add', period, accounts: paid, terms, periodEnd: sub.periodEnd, now: new Date(now) })
      : null);
  const next: PayNext | null = nc ? { ...nc, period, accounts: paid } : null;
  const what = t(period === 'year' ? 'whatYear' : 'whatMonth', { n: paid });
  const date = nc ? fmtDate(nc.date.toISOString()) : '';
  const price = nc ? fmtMoney(nc.amount, currency) : '';
  const until = sub?.periodEnd ? fmtDate(sub.periodEnd) : '';

  const call = async (url: string, method: 'PATCH' | 'POST', body?: unknown): Promise<void> => {
    o.onBusy?.(true);
    const res = await fetch(url, {
      method,
      headers: { 'content-type': 'application/json' },
      ...(body === undefined ? {} : { body: JSON.stringify(body) }),
    }).catch(() => null);
    const j = ((await res?.json().catch(() => ({}))) ?? {}) as { ok?: boolean; error?: string };
    o.onBusy?.(false);
    // Карты уже нет (удалили в другой вкладке) — страница просто освежается (§7.3: 404 no_card).
    if (!j.ok && j.error !== 'no_card') {
      payErrorBuzz();
      o.onError(t('errGeneric'));
    }
    reload();
  };
  const setAutorenew = (enabled: boolean) => void call('/api/billing/autorenew', 'PATCH', { enabled });
  const removeCard = () => void call('/api/billing/card/remove', 'POST', {});

  const sheet = (
    title: string,
    text: string,
    act: { label: string; danger?: boolean; run: () => void },
    cancel: string,
  ) =>
    drawer.open({
      title,
      sheet: true,
      lead: 'close', // вертикальный лист пути оплаты — «×» (PAY-FLOW-TZ §14.6, канон М83)
      sheetDetent: 0.4,
      body: (
        <FPane>
          <KListNote>{text}</KListNote>
          <KListBlock>
            <KButton
              action
              tone={act.danger ? 'danger' : 'primary'}
              onClick={() => {
                drawer.close();
                act.run();
              }}
            >
              {act.label}
            </KButton>
          </KListBlock>
          <KListBlock>
            <KButton action onClick={() => drawer.close()}>
              {cancel}
            </KButton>
          </KListBlock>
        </FPane>
      ),
    });

  const title = t('payAutorenew');
  let openAutorenew: (() => void) | null = null;
  if (autorenew) {
    openAutorenew = () =>
      sheet(
        title,
        native ? t('arOnTextApp', { until }) : t('arOnText', { date, price, what, until }),
        { label: t('autorenewTurnOff'), run: () => setAutorenew(false) },
        t('autorenewKeep'),
      );
  } else if (card) {
    openAutorenew = () =>
      sheet(
        title,
        native ? t('arOffCardTextApp', { card }) : t('arOffCardText', { date, price, what, card }),
        { label: t('autorenewEnable'), run: () => setAutorenew(true) },
        t('arCancel'),
      );
  } else if (!native && o.onAddCard && cardSetupPath(sub, terms?.currency ?? 'RUB') === 'stripe') {
    const add = o.onAddCard;
    openAutorenew = () =>
      sheet(title, t('arOffNoCardText', { date, price, what }), { label: t('addCardTitle'), run: add }, t('arCancel'));
  } else if (!native && o.onRenewAuto) {
    const renew = o.onRenewAuto;
    openAutorenew = () =>
      sheet(title, t('arOffNoCardRub'), { label: t('goRenew'), run: renew }, t('arCancel'));
  }
  const openCard = card
    ? () =>
        sheet(
          t('payCardPick'),
          autorenew ? `${t('cardRemoveText', { card })} ${t('cardRemoveAuto', { until })}` : t('cardRemoveText', { card }),
          { label: t('cardRemove'), danger: true, run: removeCard },
          t('autorenewKeep'),
        )
    : null;

  return {
    pro,
    autorenew,
    card,
    currency,
    next,
    addCardNote: t('addCardNote', { date, price, what }),
    openAutorenew,
    openCard,
  };
}

/** Строки «Автопродление» и «Карта» (§6.1): значение справа, «›» — когда есть шторка. */
export function ManageRows({ m }: { m: ManageState }) {
  const t = useTranslations('cabinet.billing');
  // Раздел набора (не голая группа): отступ до кнопок под ним — тот же, что между группами страницы
  // (канон UI-TEMPLATE-CANON, PAY-FLOW-TZ §14.1); своих чисел нет.
  return (
    <KListSection>
      <KListGroup>
        <KListRow
          title={t('payAutorenew')}
          value={m.autorenew ? t('autorenewValueOn') : t('autorenewValueOff')}
          chevron={!!m.openAutorenew}
          onClick={m.openAutorenew ?? undefined}
        />
        {m.card && <KListRow title={t('payCardPick')} value={m.card} chevron onClick={m.openCard ?? undefined} />}
      </KListGroup>
    </KListSection>
  );
}

/** История платежей отдельным слоем: её открывает КРУГЛАЯ КНОПКА В ШАПКЕ страницы
 *  «Подписка» (владелец 2026-08-09), а не строка в конце списка. Данные берёт сама —
 *  кнопка живёт в шапке и до состояния страницы не дотягивается. */
export function usePaymentsDrawer(): () => void {
  const drawer = useSlidePanel();
  const t = useTranslations('cabinet.billing');
  return useCallback(
    () =>
      drawer.open({
        title: t('historyToggle'),
        sheet: true,
        lead: 'close', // вертикальный лист пути оплаты — «×» (PAY-FLOW-TZ §14.6, канон М83)
        body: <PaymentsHistory />,
      }),
    [drawer, t],
  );
}

/**
 * СТРАНИЦА «ТАРИФ» — ТОЛЬКО ДЛЯ ПРИЛОЖЕНИЯ (владелец 31.08.2026). Нейтрально, одни
 * факты: имя тарифа, до какой даты активен, сколько аккаунтов и строка, что тариф из
 * приложения не меняется.
 * ЧЕГО ТУТ НЕ БЫВАЕТ НИКОГДА: цен и любых сумм, кнопки «Продлить», ссылок и QR, любого
 * указания, ГДЕ платить (Apple считает призывом даже текст без ссылки — правила 3.1.1
 * и 3.1.3, вне витрины США послаблений нет), статуса «просрочено» с призывом заплатить.
 * В вебе этой страницы нет — там живёт полная «Подписка» (BillingPanel), оплата только
 * там. Данные — та же сводка, что у «Подписки» (useSummary): второго источника нет.
 */
/** ФАКТЫ ТАРИФА — ОДНА РУКА НА ОБА МИРА (docs/NATIVE-ACCOUNT-PLAN.md, этап Б): их читают
 *  и веб-страница «Тариф» (PlanPanel ниже), и сборщик РОДНОЙ страницы приложения. Второго
 *  счёта тарифа не заводить — разъедутся слова и сроки.
 *  `ready: false` — сводка ещё едет: страница показывает скелет, а не «Бесплатно». */
export function usePlanFacts(): {
  ready: boolean;
  planName: string;
  until: string | null;
  accountsN: number;
} {
  const t = useTranslations('cabinet.billing');
  const { data } = useSummary();
  // Момент открытия — как в BillingPanel: чистота рендера, страница короткоживущая.
  const [now] = useState(() => Date.now());
  const sub = data?.subscription ?? null;
  const info = billingState(
    sub
      ? {
          status: sub.status,
          periodEnd: sub.periodEnd,
          cardBound: !!sub.cardBound,
          paidAccountsCount: sub.paidAccountsCount,
        }
      : null,
    now,
  );
  const paidCount = sub?.paidAccountsCount ?? 0;
  const isPro = paidCount > 0 && info.isCurrent;
  const trialLive = !!data?.trialUntil && new Date(data.trialUntil).getTime() > now;
  return {
    ready: !!data,
    // Имя тарифа — ТЕ ЖЕ слова, что на «Подписке»: Pro · Пробный период · Бесплатно.
    planName: isPro ? t('planPro') : trialLive ? t('nowTrial') : t('nowFree'),
    // Срок есть только у оплаченного и у пробного; у бесплатного его нет — строку не
    // рисуем вовсе, а не пишем прочерк.
    until: isPro ? (sub?.periodEnd ?? null) : trialLive ? (data?.trialUntil ?? null) : null,
    // Сколько аккаунтов даёт тариф: у Pro — оплаченные, иначе бесплатный лимит.
    accountsN: isPro ? paidCount : FREE_ACCOUNTS,
  };
}

/** Значение строки «Тарифа» приложения — прежний вид 20/650 один в один (класс .blg-now снят с
 *  веб-страниц М57; эта страница — запаска старых сборок приложения, их вид не меняем). */
const PLAN_VALUE = {
  marginLeft: 'auto',
  fontSize: 20,
  fontWeight: 650,
  color: 'var(--ink)',
} as const;

export function PlanPanel() {
  const t = useTranslations('cabinet.billing');
  const fmtDate = useFmtDate();
  const { ready, planName, until, accountsN } = usePlanFacts();
  // «Автопродление» и «Карта» (PAY-FLOW-TZ §5): управление без сумм и без входа в оплату.
  const manage = useManageSheets({ onError: () => {} });
  return (
    <DGroup className="iosf">
      <div className="ai-vgroups">
        {!ready ? (
          <div className="ai-vgroup sk-hold">
            <Sk w={150} h={22} />
            <div style={{ marginTop: 10 }}>
              <Sk w={230} h={13} />
            </div>
          </div>
        ) : (
          // ФАКТЫ, А НЕ ПОЛЯ ФОРМЫ (владелец 31.08.2026: «приведи в порядок внешний вид»).
          // Раньше тариф и число аккаунтов стояли капсулами VGroup — они читаются как
          // поля ввода, хотя менять тут нечего, да и подпись «Тариф» повторяла заголовок
          // страницы. Теперь это обычные строки свода: слева — что, справа — значение.
          //
          // ТРИ СТРОКИ «ЧТО — ЗНАЧЕНИЕ» (владелец 09.09.2026): «Активен до» — своя
          // строка, а не абзац под «Тарифом»; на тарифе без срока её нет вовсе. Тот же
          // состав, что у родной страницы приложения — экраны обязаны совпадать.
          <div className="rows">
            <div className="row">
              <div className="nm">{t('planLabel')}</div>
              <b style={PLAN_VALUE}>{planName}</b>
            </div>
            {until ? (
              <div className="row">
                <div className="nm">{t('planUntilLabel')}</div>
                <b style={PLAN_VALUE}>{fmtDate(until)}</b>
              </div>
            ) : null}
            <div className="row">
              <div className="nm">{t('kvAccounts')}</div>
              <b style={PLAN_VALUE}>{t('accountsN', { n: accountsN })}</b>
            </div>
          </div>
        )}
      </div>
      {ready && manage.pro && <ManageRows m={manage} />}
      <FHint>{t('appPlanLocked')}</FHint>
    </DGroup>
  );
}

/** Тело истории: свой запрос сводки — слой открывается из шапки, вне тела страницы. */
function PaymentsHistory() {
  const { data } = useSummary();
  const paid = data?.payments.filter((p) => p.status === 'paid') ?? [];
  return (
    <FPane>
      <PaymentsList payments={paid} />
    </FPane>
  );
}

/** Круглая кнопка «История платежей» — DOM-фолбэк для широкого вида (в шторке ту же
 *  кнопку рисует НАТИВНАЯ шапка через actionIcon). */
export function PaymentsHistoryButton() {
  const t = useTranslations('cabinet.billing');
  const open = usePaymentsDrawer();
  return (
    <KButton
      className="page-add page-round"
      title={t('historyToggle')}
      aria-label={t('historyToggle')}
      onClick={open}
      icon={
        <svg
          width="19"
          height="19"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth={2}
          strokeLinecap="round"
          strokeLinejoin="round"
          aria-hidden
        >
          <path d="M3 12a9 9 0 1 0 3-6.7" />
          <path d="M3 4v4h4" />
          <path d="M12 7v5l3 3" />
        </svg>
      }
    />
  );
}

// ─────────────────────── докупка аккаунтов ───────────────────────

/**
 * ДОКУПКА АККАУНТОВ на Pro (Э1.4, владелец 2026-08-10 «можно добавить аккаунты и
 * доплатить»): сколько добавить → котировка с сервера (доплата пропорциональна
 * остатку периода) → оплата тем же путём, что подписка.
 */
function AddAccounts({
  terms,
  startAutorenew,
  onPay,
  startWant = 1,
  head,
  onClosed,
}: {
  /** Сколько добавить по умолчанию (ТЗ ACCOUNTS-TOGGLE §3.3: из подключения — `need − paid`). */
  startWant?: number;
  /** Строка сверху («Оплачено 2, нужно 3») — докупка из подключения сверх оплаченных. */
  head?: string;
  /** Слой снят (оплатой или «×»): `bought` — нажимали ли оплату. Подключение по нему ждёт оплату или отменяется. */
  onClosed?: (bought: boolean) => void;
  /** Цены в валюте юзера — сумма следующего списания за новое число аккаунтов (§2.2 п.3). */
  terms: PayTerms | null;
  /** Положение «Автопродления» по умолчанию (§2.2: как у подписки). */
  startAutorenew: boolean;
  /** Кнопка расчёта → полушторка подтверждения (PAY-FLOW-TZ §14.3): сколько добавить, сумма и валюта
   *  котировки, следующее списание, конец оплаченного срока («1 аккаунт до 2 октября 2027 г.») и выбор
   *  автопродления, который держит этот экран. */
  onPay: (added: number, price: number, currency: Currency, next: PayNext, until: string | null, auto: AutoChoice) => void;
}) {
  const t = useTranslations('cabinet.billing');
  const fmtMoney = useFmtMoney();
  const [now] = useState(() => new Date());
  const [autorenew, setAutorenew] = useState(startAutorenew);
  const leftLabel = (m: number, d: number): string =>
    m > 0 && d > 0 ? t('addLeftMD', { m, d }) : m > 0 ? t('addLeftM', { m }) : t('addLeftD', { d });
  // ЧИСЛО И КОТИРОВКА БЕЗ ПРЫЖКОВ (О8): слой открывается уже с котировкой на 1 аккаунт (её
  // ждёт кнопка «Добавить аккаунты»); выбор другого числа тянет его котировку, а до её прихода
  // экран показывает прежнюю — строки на месте, меняются только числа. `want` — выбранное,
  // `shownN` — чья котировка сейчас на экране.
  const [want, setWant] = useState(startWant);
  const [shownN, setShownN] = useState(startWant);
  // Слой снят — сказать подключению, нажимали ли оплату (ТЗ ACCOUNTS-TOGGLE §3.3).
  const boughtRef = useRef(false);
  const closedRef = useRef(onClosed);
  useEffect(() => {
    closedRef.current = onClosed;
  }, [onClosed]);
  useEffect(() => () => closedRef.current?.(boughtRef.current), []);
  const [rawWant, rawShown] = usePersistentPeek<unknown>([addQuoteKey(want), addQuoteKey(shownN)]);
  const wantQuote = parseAddQuote(rawWant);
  // Котировка выбранного приехала — она и на экране (синхронизация в рендере, без эффекта).
  if (wantQuote && shownN !== want) setShownN(want);
  const quote = wantQuote ?? parseAddQuote(rawShown);
  const add = wantQuote ? want : shownN;
  const pick = (n: number) => {
    refreshBilling(addQuoteKey(n), addQuoteUrl(n));
    setWant(n);
  };
  // Открыли — освежить котировку на месте (кэш мог устареть; числа меняются, строки — нет).
  useEffect(() => {
    refreshBilling(addQuoteKey(startWant), addQuoteUrl(startWant));
  }, [startWant]);
  // Кнопка расчёта открывает полушторку подтверждения (§14.3): автопродление выбирается там.
  const buy = () => {
    if (!quote || !wantQuote || !next) return;
    boughtRef.current = true;
    onPay(add, quote.price, quote.currency, next, quote.periodEnd, { value: autorenew, set: setAutorenew });
  };

  // Составляющие — в валюте котировки, с сервера; старый ответ без них — рублёвые и только при RUB.
  const rubQuote = !quote || quote.currency === 'RUB';
  const perMonth = !quote ? null : (quote.perAccountMonth ?? (rubQuote ? quote.perAccountMonthRub : null));
  const perOne = !quote ? null : (quote.perAddedAccount ?? (rubQuote ? quote.perAddedAccountRub : null));
  // Следующее списание за новое число аккаунтов (§2.2 п.3): дата — конец периода подписки, сумма —
  // полная цена срока по условиям юзера (`periodPrice`), не доплата.
  const nc =
    quote && terms && quote.period
      ? nextCharge({ action: 'add', period: quote.period, accounts: quote.newCount, terms, periodEnd: quote.periodEnd, now })
      : null;
  const next: PayNext | null = nc && quote?.period ? { ...nc, period: quote.period, accounts: quote.newCount } : null;

  return (
    // Канон Apple (М57): раздел строк «что — значение», выбор числа — PopSelect в значении строки,
    // условие доплаты — сноской под группой; кнопка — в прибитом подвале (§14.2).
    <FPane className="pay-calc">
      {head && <FHint>{head}</FHint>}
      <KListSection label={t('addHead')}>
        <KListGroup>
          <KListRow
            title={t('addHowMany')}
            control
            value={
              <PopSelect
                align="right"
                label={t('accountsN', { n: want })}
                ariaLabel={t('addHowMany')}
                options={[...new Set([1, 2, 3, 4, 5, 10, startWant])].sort((a, b) => a - b).map((n) => ({
                  key: String(n),
                  label: t('accountsN', { n }),
                  on: n === want,
                }))}
                onPick={(k) => pick(Math.max(1, Number(k) || 1))}
              />
            }
          />
          {quote && (
            <KListRow title={t('addTotalAfter')} value={t('accountsN', { n: quote.newCount })} />
          )}
        </KListGroup>
      </KListSection>
      {/* СТРОКИ РАСЧЁТА ДОПЛАТЫ (ТЗ WEB-CAUSES М78, владелец 06.10.2026: «иначе вопрос „почему так
          много“»): цена добавляемого аккаунта в месяц, скидка года, остаток периода, доплата за один
          и итог. Числа — из котировки сервера (accountAddQuote и составляющие в валюте юзера),
          сами здесь не считаем. */}
      <KListSection
        label={t('calcHead')}
        hint={quote?.period === 'year' ? `${t('addFine')} ${t('addFineYear')}` : t('addFine')}
      >
        {/* Тот же вид, что у «Расчёта» оплаты (§16 К16): свёрнуто — итог доплаты в шапке, развёрнуто — составляющие. */}
        <KDisclosure
          className="bill-calc"
          title={t('addTotalN', { n: add })}
          value={quote ? fmtMoney(quote.price, quote.currency) : '…'}
        >
          {perMonth != null && quote && (
            <KListRow
              title={t('addPerAccMonth')}
              value={t('calcPerMonthVal', { price: fmtMoney(perMonth, quote.currency) })}
            />
          )}
          {/* Д5 (владелец 07.10.2026: «почему скидка 20 % в докупке — непонятно»): подписка
              годовая → добавляемый аккаунт идёт по годовой цене; сноска объясняет почему. */}
          {quote && quote.discountPct > 0 && (
            <KListRow title={t('addYearPrice', { pct: quote.discountPct })} />
          )}
          {quote?.monthsLeft != null && (
            <KListRow title={t('addLeft')} value={leftLabel(quote.monthsLeft, quote.daysLeft)} />
          )}
          {perOne != null && quote && (
            <KListRow title={t('addPerOne')} value={fmtMoney(perOne, quote.currency)} />
          )}
        </KDisclosure>
      </KListSection>
      <CalcFoot note={<CalcSecure currency={quote?.currency ?? terms?.currency ?? 'RUB'} />}>
        <KBareButton
          className="k-btn--action cta-grad"
          // Пока котировка выбранного числа не приехала, кнопка ждёт (на экране — прежние числа).
          disabled={!quote || !wantQuote || !next}
          onClick={buy}
        >
          {t('addPayCta', { price: quote ? fmtMoney(quote.price, quote.currency) : '' })}
        </KBareButton>
      </CalcFoot>
    </FPane>
  );
}

// ─────────────── подвал экрана расчёта и полушторка подтверждения (PAY-FLOW-TZ §14.2, §14.3) ───────────────

/** ПОДВАЛ ЭКРАНА РАСЧЁТА (§14.2): кнопка прибита к низу листа — рецепт планки мастера (`.wiz-foot`:
 *  на телефоне прибита к полу и залита фоном листа, на десктопе — к низу панели), содержимое прокручивается
 *  над ней (`.pay-calc` даёт добег). Своих размеров здесь нет. */
export function CalcFoot({ children, note }: { children: ReactNode; note?: ReactNode }) {
  return (
    <div className="wiz-foot pay-foot">
      <div className="wiz-foot-row">{children}</div>
      {note}
    </div>
  );
}

/** Строка защиты под кнопкой экрана расчёта (§18.1 п.7) и витрины Pro (TARIFF-PRO-2-TZ §3.5) — касса по валюте. */
export function CalcSecure({ currency }: { currency: Currency }) {
  const t = useTranslations('cabinet.billing');
  return <PayLockNote text={t(currency === 'RUB' ? 'paySecureRub' : 'paySecureEur')} />;
}

/** Строка оферты под кнопкой экрана подтверждения (§18.1 п.5): «Оплачивая, вы принимаете условия оферты»; оферта —
 *  слоем стопки, не новой вкладкой; при автопродлении — сразу на пункте «Автопродление» (`/oferta#autorenew`). */
function useOfertaLine(): (autorenew: boolean) => ReactNode {
  const t = useTranslations('cabinet.billing');
  const ts = useTranslations('cabinet.settings');
  const locale = useLocale();
  const drawer = useSlidePanel();
  return (autorenew) =>
    t.rich('checkOferta', {
      a: (chunks) => (
        <a
          href={`/${locale}/oferta${autorenew ? '#autorenew' : ''}`}
          onClick={(e) => {
            e.preventDefault();
            drawer.open({
              title: ts('termsRow'),
              sheet: true,
              lead: 'close',
              body: <TermsLayerAt anchor={autorenew ? 'autorenew' : undefined} />,
            });
          }}
        >
          {chunks}
        </a>
      ),
    });
}

/** Выбор автопродления, который держит экран расчёта (хозяин полушторки, §14.3 п.2). */
interface AutoChoice {
  value: boolean;
  set: (on: boolean) => void;
}

// ─────────────────────── личная скидка (PRICES-TZ §6) ───────────────────────

/** Меньше этого до конца скидки — показываем обратный отсчёт (PRICES-TZ §6). */
const DISCOUNT_COUNTDOWN_MS = 48 * 3_600_000;

/** Текущий момент для цены и отсчёта: у скидки со сроком — обновляется раз в минуту, чтобы отсчёт
 *  шёл живьём, а после истечения строка исчезла и цена стала полной. */
function useDiscountNow(terms: PayTerms | null): Date {
  const [now, setNow] = useState(() => new Date());
  const untilMs =
    terms && terms.discountPct > 0 && terms.discountUntil ? new Date(terms.discountUntil).getTime() : null;
  useEffect(() => {
    if (untilMs === null) return;
    const id = window.setInterval(() => setNow(new Date()), 60_000);
    return () => window.clearInterval(id);
  }, [untilMs]);
  return now;
}

/** Строка личной скидки (PRICES-TZ §6, приёмка Ц2): заголовок — «Скидка N %»; вторая серая строка — срок:
 *  меньше 48 ч до конца — отсчёт «осталось Ч ч М мин», иначе «до ДД.ММ.ГГГГ» (дата цифрами); у бессрочной
 *  второй строки нет. Скидка не действует на `now` или это iOS-приложение (цен там нет, CLAUDE.md п.7) — null. */
function useDiscountText(terms: PayTerms | null, now: Date): { title: string; left: string | null } | null {
  const tp = useTranslations('onboarding.pay');
  const fmtDateShort = useFmtDateShort();
  const native = useNativeApp();
  if (native || !terms || !discountActive(terms, now)) return null;
  const title = tp('promoBanner', { pct: terms.discountPct });
  if (!terms.discountUntil) return { title, left: null };
  const until = new Date(terms.discountUntil);
  const ms = until.getTime() - now.getTime();
  if (ms >= DISCOUNT_COUNTDOWN_MS) return { title, left: tp('promoBannerLeft', { until: fmtDateShort(until.toISOString()), time: '' }) };
  const mins = Math.max(1, Math.ceil(ms / 60_000));
  const h = Math.floor(mins / 60);
  const m = mins % 60;
  const time = h > 0 ? tp('promoTimeHMS', { h, m }) : tp('promoTimeMS', { m });
  return { title, left: tp('promoBannerLeft', { until: 'none', time }) };
}

// ─────────────────────── экран оплаты ───────────────────────

/**
 * ЭКРАН ОПЛАТЫ (владелец 2026-08-10): страница тарифа только сравнивает и зовёт, а
 * ЧТО покупаем — здесь. Своё состояние: число аккаунтов, срок, оплата токенами.
 * Кнопка открывает слой «Оплата» стопкой над расчётом (PAY-SCREEN-2-TZ §1.1) — шторки
 * подтверждения нет; карта, её сохранение и продление решаются в слое.
 */
export function PayScreen({
  suggested,
  connected = 0,
  startPeriod,
  terms,
  payableTokens,
  isCurrent,
  paidCount,
  periodEnd,
  startAutorenew,
  onPay,
}: {
  suggested: number;
  /** Сколько аккаунтов подключено (ТЗ ACCOUNTS-TOGGLE §3.2): минимум выбора; продление — за max(оплачено, подключено). */
  connected?: number;
  startPeriod: Period;
  /** Цены в валюте юзера (Э5.1); null — сводка без условий (старый кэш). */
  terms: PayTerms | null;
  /** Сколько токенов можно списать; 0 — строки «Оплатить токенами» нет (и всегда 0 не в ₽). */
  payableTokens: number;
  isCurrent: boolean;
  /** Сколько аккаунтов уже оплачено (Р1). */
  paidCount: number;
  /** Конец оплаченного периода — от него считается дата следующего списания при продлении. */
  periodEnd: string | null;
  /** Положение «Автопродления» по умолчанию (§2.1: €/$ — вкл, ₽ — как у подписки). */
  startAutorenew: boolean;
  /** Кнопка расчёта → экран подтверждения (PAY-FLOW-TZ §14.3): срок, число, токены и выбор автопродления, который
   *  держит этот экран; суммы и следующее списание считает `startPayCheck` той же функцией `payCalc` (§3.4). */
  onPay: (period: Period, accounts: number, useTokens: boolean, auto: AutoChoice) => void;
}) {
  const t = useTranslations('cabinet.billing');
  const fmtDateShort = useFmtDateShort();
  const fmtMoneyIn = useFmtMoney();
  // Момент расчёта: при скидке со сроком — живой, раз в минуту (PRICES-TZ §6: отсчёт и полная цена
  // сразу после истечения).
  const now = useDiscountNow(terms);
  const disc = useDiscountText(terms, now);
  const [autorenew, setAutorenew] = useState(startAutorenew);
  const currency: Currency = terms?.currency ?? 'RUB';
  const fmtMoney = (n: number): string => fmtMoneyIn(n, currency);
  // РЕЖИМ ПРОДЛЕНИЯ (MULTICURRENCY-PLAN Э0.1, владелец 07.10.2026: «в продлить я не могу
  // выбирать количество аккаунтов — у меня есть два аккаунта, я могу продлить срок»):
  // оплаченный срок идёт и аккаунты оплачены → число аккаунтов = оплаченному, выбирается
  // только срок. Число меняет «Добавить аккаунты». Сервер держит то же правило (subscribe →
  // 400 accounts_locked). Пробный, бесплатный, просрочка — выбор числа как раньше.
  const renewMode = isCurrent && paidCount >= 1;
  // МЕНЬШЕ ПОДКЛЮЧЁННЫХ НЕЛЬЗЯ (ТЗ ACCOUNTS-TOGGLE §3.2): минимум и умолчание — max(1, подключено);
  // продление — за max(оплачено, подключено), сервер держит то же (subscribe → accounts_below_connected).
  const minAccounts = Math.max(1, connected);
  const [accountsSel, setAccounts] = useState(Math.max(suggested, minAccounts));
  const accounts = renewMode ? Math.max(paidCount, connected) : Math.max(accountsSel, minAccounts);
  const grew = renewMode && connected > paidCount;
  const [period, setPeriod] = useState<Period>(startPeriod);
  const [useTokens, setUseTokens] = useState(false);

  // Расчёт — та же функция, что у подтверждения (`payCalc`, TARIFF-PRO-2-TZ §3.4): второй копии формулы нет.
  const action: PayAction = renewMode ? 'renew' : 'first';
  const { price, tokensUsed, cash, next } = payCalc({ terms, period, accounts, payableTokens, useTokens, action, periodEnd, now });
  // Цена без личной скидки — зачёркнутой в строке «Скидка» (та же формула, скидка 0).
  const fullPrice = terms ? periodPrice({ ...terms, discountPct: 0 }, accounts, period, now) : price;
  // Промежуточный итог (§16 К16) — сумма до ВСЕХ скидок: (первый + шаг × (n − 1)) × месяцев.
  const subtotal = terms ? periodPrice({ ...terms, discountPct: 0, yearDiscountPct: 0 }, accounts, period, now) : price;
  const yearDisc = period === 'year' && (terms?.yearDiscountPct ?? 0) > 0;

  return (
    // Канон Apple (М57): что оплачиваем — строки набора, выборы — PopSelect/KSwitch в значении
    // строки, итог — последней строкой; кнопка роли «действие» — в прибитом подвале (§14.2).
    // Рубли — только тут и в слое «Оплата»: это момент оплаты (CLAUDE.md «Позиционирование» п.6).
    <FPane className="pay-calc">
      <KListSection label={t('payScreenWhat')}>
        <KListGroup>
          {renewMode ? (
            // Продление — число значением, без выбора (Э0.1).
            <KListRow
              title={t('accountsLabel')}
              value={t('accountsN', { n: accounts })}
              {...(grew ? { hint: t('accountsGrew', { n: connected }) } : {})}
            />
          ) : (
            // ЦЕНА ЗА АККАУНТ (ТЗ USERTEST-1014 п.18): та же подсказка и тот же источник цен, что у витрины
            // (terms.first / step в валюте тарифа).
            <KListRow
              title={t('accountsLabel')}
              control
              {...(terms ? { hint: t('accountsPriceHint', { first: fmtMoney(terms.first), next: fmtMoney(terms.step) }) } : {})}
              value={
                <PopSelect
                  align="right"
                  label={t('accountsN', { n: accounts })}
                  ariaLabel={t('accountsLabel')}
                  options={accountOptionsFrom(ACCOUNT_OPTIONS, connected).map((n) => ({
                    key: String(n),
                    label: t('accountsN', { n }),
                    on: n === accounts,
                  }))}
                  onPick={(k) => setAccounts(Math.max(minAccounts, Number(k) || 1))}
                />
              }
            />
          )}
          <KListRow
            title={t('periodLabel')}
            control
            value={
              <PopSelect
                align="right"
                label={
                  period === 'year' && (terms?.yearDiscountPct ?? 0) > 0
                    ? t('periodYearOpt', { pct: terms?.yearDiscountPct ?? 0 })
                    : t(`periods.${period}`)
                }
                ariaLabel={t('periodLabel')}
                options={PERIODS.map((p) => ({
                  key: p,
                  label:
                    p === 'year' && (terms?.yearDiscountPct ?? 0) > 0
                      ? t('periodYearOpt', { pct: terms?.yearDiscountPct ?? 0 })
                      : t(`periods.${p}`),
                  on: p === period,
                }))}
                onPick={(k) => setPeriod(k as Period)}
              />
            }
          />
          {payableTokens > 0 && (
            <KListRow
              title={t('tokensPayLabel')}
              control
              value={
                <KSwitch
                  checked={useTokens}
                  onChange={setUseTokens}
                  ariaLabel={t('tokensPayLabel')}
                />
              }
            />
          )}
          {!terms && (
            <KListRow title={t(period === 'year' ? 'totalYear' : 'totalMonth')} value={fmtMoney(cash)} />
          )}
        </KListGroup>
      </KListSection>
      {/* СТРОКИ РАСЧЁТА (ТЗ WEB-CAUSES М78, владелец 06.10.2026: «за N аккаунтов, столько-то за
          каждый, ниже — суммарная сумма»). Цена по условиям юзера НЕ плоская: первый аккаунт —
          first, каждый следующий — step в валюте юзера (packages/db terms.ts accountsMonth),
          поэтому строк «за аккаунт» две; год — ×12 со скидкой. Произведение строк = итог (priceOf). */}
      {terms && (
        // РАСЧЁТ — СВОРАЧИВАЕМОЙ КАРТОЧКОЙ (PAY-FLOW-TZ §16 К16, владелец 10.10: «в выпадашку, по умолчанию закрытой,
        // и распиши подробнее»; образец — «Context window» листа «Usage» Claude). Свёрнуто — одна строка-шапка «Итого
        // за год/месяц — сумма ˇ»; развёрнуто — первый аккаунт, ещё N, месяцев, промежуточный итог до скидок и каждая
        // скидка со зачёркнутой суммой строки выше и новой суммой; строки плотные, без волосков (владелец 10.10:
        // «компактнее, не городить палки между каждым»); итога внизу нет — он в шапке (§18.9 п.1).
        <KListSection label={t('calcHead')}>
          <KDisclosure
            className="bill-calc"
            title={t(period === 'year' ? 'calcTotalYear' : 'calcTotalMonth')}
            // Подпись серым (владелец 10.10: «столько-то аккаунтов до такого-то числа, две скидки 20 и 25 %»); скидки —
            // всегда второй строкой (§18.9 п.3), без скидок — одна строка.
            hint={[
              t('calcSumAccounts', { n: accounts, until: fmtDateShort(next.date.toISOString()) }),
              yearDisc && disc
                ? t('calcSumDiscounts2', { a: terms.yearDiscountPct, b: terms.discountPct })
                : yearDisc
                  ? t('calcSumDiscount1', { pct: terms.yearDiscountPct })
                  : disc
                    ? t('calcSumDiscount1', { pct: terms.discountPct })
                    : null,
            ]
              .filter((x): x is string => !!x)
              .map((line) => (
                <span key={line}>{line}</span>
              ))}
            value={fmtMoney(price)}
          >
            <KListRow
              title={t('calcFirst')}
              value={t('calcPerMonthVal', { price: fmtMoney(terms.first) })}
            />
            {accounts > 1 && (
              <KListRow
                title={t('calcNextN', { n: accounts - 1 })}
                value={t('calcNextVal', { n: accounts - 1, price: fmtMoney(terms.step) })}
              />
            )}
            {period === 'year' && <KListRow title={t('calcMonths')} value="12" />}
            {(yearDisc || disc) && <KListRow title={t('calcSubtotal')} value={fmtMoney(subtotal)} />}
            {yearDisc && (
              <KListRow
                className="bill-discount"
                title={t('calcYearDiscount', { pct: terms.yearDiscountPct })}
                hint={t('calcYearDiscountHint')}
                value={
                  <>
                    <s>{fmtMoney(subtotal)}</s> {fmtMoney(fullPrice)}
                  </>
                }
              />
            )}
            {/* ЛИЧНАЯ СКИДКА (PRICES-TZ §6, §11.2 п.3): «Скидка N %», срок подстрокой (меньше 48 ч — отсчёт); справа
                сумма строкой выше зачёркнута и сумма со скидкой. В приложении строки нет. */}
            {disc && (
              <KListRow
                className="bill-discount"
                title={disc.title}
                hint={disc.left ?? undefined}
                value={
                  <>
                    <s>{fmtMoney(fullPrice)}</s> {fmtMoney(price)}
                  </>
                }
              />
            )}
            {tokensUsed > 0 && <KListRow title={t('calcToPay')} value={fmtMoney(cash)} />}
          </KDisclosure>
        </KListSection>
      )}
      <CalcFoot note={<CalcSecure currency={currency} />}>
        <KBareButton
          className="k-btn--action cta-grad"
          onClick={() => onPay(period, accounts, useTokens, { value: autorenew, set: setAutorenew })}
        >
          {t(renewMode ? 'payCtaRenew' : 'payCtaCalc', { price: fmtMoney(cash) })}
        </KBareButton>
      </CalcFoot>
    </FPane>
  );
}

// ─────────────────────────── шаг подтверждения телефона ───────────────────────────

interface PhoneInit {
  deepLink: string;
  code: string;
  botUsername: string;
  token: string;
}

// Подтверждение телефона внутри модалки оплаты: без номера платёж не создаётся (гейт
// subscribe/route.ts). Переиспользуем онбординг-флоу — Telegram-бот и роуты
// /api/user/phone/init|status. Как только номер подтверждён — onVerified повторяет оплату.
function PhoneVerifyStep({
  onVerified,
  onCancel,
}: {
  onVerified: () => void;
  onCancel: () => void;
}) {
  const t = useTranslations('cabinet.billing');
  const tp = useTranslations('onboarding.phone');
  const [init, setInit] = useState<PhoneInit | null>(null);
  const [error, setError] = useState<string | null>(null);
  // onVerified — новая замыкашка каждый рендер (зовёт pay(period)); держим в ref,
  // чтобы эффекты init/поллинга не перезапускались и не плодили сессии.
  const onVerifiedRef = useRef(onVerified);
  onVerifiedRef.current = onVerified;

  // Создаём сессию подтверждения один раз.
  useEffect(() => {
    let alive = true;
    fetch('/api/user/phone/init', { method: 'POST' })
      .then((r) => r.json())
      .then((j) => {
        if (!alive) return;
        if (j?.verified) return onVerifiedRef.current();
        if (j?.ok && j.deepLink) setInit(j as PhoneInit);
        else setError(tp('errors.initFailed'));
      })
      .catch(() => alive && setError(tp('errors.network')));
    return () => {
      alive = false;
    };
    // tp стабилен в рамках локали; onVerified — через ref
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Поллим статус, пока номер не подтверждён.
  useEffect(() => {
    if (!init) return;
    const timer = setInterval(async () => {
      try {
        const r = await fetch(`/api/user/phone/status?token=${init.token}`, { cache: 'no-store' });
        const j = (await r.json()) as { status?: string };
        if (j.status === 'verified') {
          clearInterval(timer);
          onVerifiedRef.current();
        } else if (j.status === 'conflict') {
          setError(tp('errors.conflict'));
        }
      } catch {
        /* ретрай */
      }
    }, 3000);
    return () => clearInterval(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [init]);

  // Канон Apple (М57): пояснение и шаги — сноской и группой строк, кнопки — роль «действие»
  // одной ширины (ссылка на бота — тот же рецепт набора .k-btn, это <a>, не кнопка).
  return (
    <FPane>
      {error && <DError>{error}</DError>}
      <KListSection label={t('phoneNote')}>
        {/* Шаги — фразами сноски (строка списка режет текст «…» в одну строку). */}
        <KListNote>1. {tp('steps.open')}</KListNote>
        <KListNote>2. {tp('steps.share')}</KListNote>
        <KListNote>3. {tp('steps.return')}</KListNote>
      </KListSection>
      {init ? (
        <>
          <KListBlock>
            <a
              className="k k-ctl k-btn k-btn--primary k-btn--action"
              href={init.deepLink}
              target="_blank"
              rel="noreferrer"
            >
              {tp('openBot')}
            </a>
          </KListBlock>
          <KListNote>
            {tp.rich('manual', {
              bot: init.botUsername,
              code: init.code,
              b: (chunks) => <b>{chunks}</b>,
              vp: (chunks) => (
                <b style={{ fontFamily: 'ui-monospace, monospace', letterSpacing: 1 }}>{chunks}</b>
              ),
            })}
          </KListNote>
        </>
      ) : (
        !error && <KListNote>{tp('preparing')}</KListNote>
      )}
      <KListBlock>
        <KButton action onClick={onCancel}>
          {t('phoneCancel')}
        </KButton>
      </KListBlock>
    </FPane>
  );
}

// История: только фактические движения (paid) — «ожидается»/«не прошёл» не движения.
function PaymentsList({
  payments,
}: {
  payments: {
    id: string;
    kind: string;
    amountRub: number;
    amount?: number | null;
    currency?: string | null;
    createdAt: string;
    provider?: string;
    source?: string;
    /** Способ ключом (§2.4): card | apple_pay | google_pay | link | paypal | sbp_card | tokens. */
    method?: string | null;
  }[];
}) {
  const t = useTranslations('cabinet.billing');
  const fmtDateTime = useFmtDateTime();
  const fmtMoney = useFmtMoney();
  // Сумма — в валюте самого платежа (Э5.4): amount+currency, у старых — amountRub в рублях.
  const money = (p: { amount?: number | null; amountRub: number; currency?: string | null }): string => {
    const m = paymentMoney({ amount: p.amount ?? null, amountRub: p.amountRub, currency: p.currency ?? null });
    return fmtMoney(m.amount, m.currency);
  };
  // Канон Apple (М57): одна группа строк — что за платёж, когда (подстрокой) и сумма справа.
  if (payments.length === 0) return <KListNote>{t('noHistory')}</KListNote>;
  return (
    <KListGroup>
      {payments.map((p) => (
        <KListRow
          key={p.id}
          title={
            <>
              {p.kind === 'subscription' ? t('paymentSubscription') : t('paymentTopup')}
              {/* платёж из Telegram-бота (TG-BOT-AI-PLAN §9.2) */}
              {p.source === 'telegram' && <span className="ai-src-badge">{t('sourceBot')}</span>}
            </>
          }
          hint={(() => {
            // Способ словом рядом с датой (PAY-SCREEN-TZ §2.4): «Карта», «Apple Pay», «PayPal»…
            const k = payMethodWordKey(p.method);
            return k ? `${fmtDateTime(p.createdAt)} · ${t(`method.${k}`)}` : fmtDateTime(p.createdAt);
          })()}
          value={
            /* звёзды: amountRub — номинал зачисленных токенов, а не рубли;
               provider='tokens' — подписка оплачена целиком токен-балансом (5.5) */
            p.provider === 'stars' ? (
              t('starsAmount', { n: p.amountRub })
            ) : p.provider === 'tokens' ? (
              <Tok n={p.amountRub} size={11} />
            ) : (
              money(p)
            )
          }
        />
      ))}
    </KListGroup>
  );
}
